/**
 * L7 Platform — progressive tab enhancement for opted-in feature lists.
 *
 * Transforms `.l7-feature-list.l7-tabs` / `.l7-why-section .l7-feature-list`
 * (or a wrapper carrying `.l7-tabs`) into a horizontal tab controller: a
 * `role="tablist"` of tiles built from each item's title, with panels toggled
 * on click / keyboard. Without JS the list renders exactly as authored — no
 * content loss. Never mutates the Visual Builder DOM (`body.et-fb`).
 */
(function (window, document) {
	'use strict';

	var SELECTOR =
		'.l7-feature-list.l7-tabs, .l7-tabs .l7-feature-list, .l7-why-section .l7-feature-list';

	/**
	 * @return {boolean}
	 */
	function isVisualBuilder() {
		return document.body && document.body.classList.contains('et-fb');
	}

	/**
	 * Unique id fragment for aria wiring.
	 *
	 * @param {number} index List index on the page.
	 * @return {string}
	 */
	function uid(index) {
		return 'l7-tabs-' + index;
	}

	/**
	 * Collect Divi feature child wrappers for a list root.
	 *
	 * @param {Element} list `.l7-feature-list` root.
	 * @return {Element[]}
	 */
	function collectItems(list) {
		var itemsRoot = list.querySelector('.l7-feature-list__items');
		if (!itemsRoot) {
			return [];
		}

		var wrappers = itemsRoot.querySelectorAll(
			':scope > .et_pb_module.l7_feature, :scope > .l7_feature, :scope > .l7-feature-list__item'
		);

		if (wrappers.length) {
			return Array.prototype.slice.call(wrappers);
		}

		return Array.prototype.slice.call(
			itemsRoot.querySelectorAll('.l7-feature-list__item')
		);
	}

	/**
	 * Resolve the inner `.l7-feature-list__item` (or the node itself).
	 *
	 * @param {Element} wrapper Child wrapper or item.
	 * @return {Element|null}
	 */
	function resolveItem(wrapper) {
		if (wrapper.classList.contains('l7-feature-list__item')) {
			return wrapper;
		}
		return wrapper.querySelector('.l7-feature-list__item');
	}

	/**
	 * Title text for a feature child.
	 *
	 * @param {Element} wrapper Child wrapper or item.
	 * @return {string}
	 */
	function itemTitle(wrapper) {
		var item = resolveItem(wrapper) || wrapper;
		var title = item.querySelector('.l7-feature-list__title');
		return title ? title.textContent.replace(/\s+/g, ' ').trim() : '';
	}

	/**
	 * Activate one tab / panel pair; deactivate the rest.
	 *
	 * @param {Element[]} tabs Tab buttons.
	 * @param {Element[]} panels Panel wrappers.
	 * @param {number} index Active index.
	 * @param {boolean} focus Whether to focus the active tab.
	 * @return {void}
	 */
	function activate(tabs, panels, index, focus) {
		var i;
		for (i = 0; i < tabs.length; i++) {
			var selected = i === index;
			tabs[i].setAttribute('aria-selected', selected ? 'true' : 'false');
			tabs[i].tabIndex = selected ? 0 : -1;
			tabs[i].classList.toggle('is-active', selected);
			if (selected && focus) {
				tabs[i].focus();
			}

			if (panels[i]) {
				if (selected) {
					panels[i].removeAttribute('hidden');
					panels[i].classList.add('is-active');
				} else {
					panels[i].setAttribute('hidden', '');
					panels[i].classList.remove('is-active');
				}
			}
		}
	}

	/**
	 * Enhance a single opted-in feature list into a tab controller.
	 *
	 * @param {Element} list `.l7-feature-list` root.
	 * @param {number} listIndex Index among enhanced lists.
	 * @return {void}
	 */
	function enhanceList(list, listIndex) {
		if (list.getAttribute('data-l7-tabs') === '1') {
			return;
		}

		var wrappers = collectItems(list);
		if (wrappers.length < 2) {
			return;
		}

		var itemsRoot = list.querySelector('.l7-feature-list__items');
		var base = uid(listIndex);
		var heading = list.querySelector('.l7-feature-list__heading');
		var eyebrow = list.querySelector('.l7-feature-list__eyebrow');
		var tablistLabel = '';

		if (heading) {
			tablistLabel = heading.textContent.replace(/\s+/g, ' ').trim();
		}
		if (!tablistLabel && eyebrow) {
			tablistLabel = eyebrow.textContent.replace(/\s+/g, ' ').trim();
		}
		if (!tablistLabel) {
			tablistLabel = 'Content tabs';
		}

		var nav = document.createElement('div');
		nav.className = 'l7-tabs__nav';
		nav.setAttribute('role', 'tablist');
		nav.setAttribute('aria-label', tablistLabel);

		var tabs = [];
		var panels = [];
		var i;

		for (i = 0; i < wrappers.length; i++) {
			var wrapper = wrappers[i];
			var label = itemTitle(wrapper) || 'Item ' + (i + 1);
			var tabId = base + '-tab-' + i;
			var panelId = base + '-panel-' + i;

			var tab = document.createElement('button');
			tab.type = 'button';
			tab.className = 'l7-tabs__tab';
			tab.setAttribute('role', 'tab');
			tab.id = tabId;
			tab.setAttribute('aria-controls', panelId);
			tab.setAttribute('aria-selected', 'false');
			tab.tabIndex = -1;
			tab.textContent = label;
			nav.appendChild(tab);
			tabs.push(tab);

			wrapper.classList.add('l7-tabs__panel');
			wrapper.setAttribute('role', 'tabpanel');
			wrapper.id = panelId;
			wrapper.setAttribute('aria-labelledby', tabId);
			wrapper.setAttribute('hidden', '');
			panels.push(wrapper);
		}

		if (heading && heading.nextSibling) {
			list.insertBefore(nav, heading.nextSibling);
		} else if (itemsRoot) {
			list.insertBefore(nav, itemsRoot);
		} else {
			list.insertBefore(nav, list.firstChild);
		}

		list.classList.add('l7-tabs--enhanced');
		list.setAttribute('data-l7-tabs', '1');

		// Items root becomes a single-panel stack once enhanced.
		if (itemsRoot) {
			itemsRoot.classList.add('l7-tabs__panels');
		}

		activate(tabs, panels, 0, false);

		nav.addEventListener('click', function (event) {
			var target = event.target;
			if (!target || !target.closest) {
				return;
			}
			var button = target.closest('[role="tab"]');
			if (!button || !nav.contains(button)) {
				return;
			}
			event.preventDefault();
			activate(tabs, panels, tabs.indexOf(button), false);
		});

		nav.addEventListener('keydown', function (event) {
			var current = document.activeElement;
			var index = tabs.indexOf(current);
			if (index < 0) {
				return;
			}

			var next = index;
			switch (event.key) {
				case 'ArrowRight':
					next = (index + 1) % tabs.length;
					break;
				case 'ArrowLeft':
					next = (index - 1 + tabs.length) % tabs.length;
					break;
				case 'Home':
					next = 0;
					break;
				case 'End':
					next = tabs.length - 1;
					break;
				default:
					return;
			}

			event.preventDefault();
			activate(tabs, panels, next, true);
		});
	}

	/**
	 * @return {void}
	 */
	function init() {
		if (isVisualBuilder()) {
			return;
		}

		var lists = document.querySelectorAll(SELECTOR);
		var seen = [];
		var i;

		for (i = 0; i < lists.length; i++) {
			var list = lists[i];
			if (seen.indexOf(list) !== -1) {
				continue;
			}
			seen.push(list);
			enhanceList(list, seen.length - 1);
		}
	}

	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', init);
	} else {
		init();
	}
})(window, document);
